import assetsApi, { fetchAssets, findAsset, saveAsset, deleteAssets, fetchAssetsCountByKind } from './assets.api.js';
import { notificate } from '../../services/notification.js';
import powerTableService from "../../services/powerTable.js";
import { get as getStore } from 'svelte/store';
import { format } from 'svelte-i18n';
import tagService from "../tag/service.js";
import { sortIPAddress } from '../../js/utils.js';
import { assetsLimits } from '../user/user.state.svelte.js';
import API from '../../services/api.svelte.js';

/* Declare assets data here to avoid a VSO annoying highlight */

const assetsData = $state([]);
// const allAssetsData = $state([]);
const assetsListData = $state([])

/* Assets state structure */

export const assets = $state({
    data: assetsData,
    // allData: {
    //     updatedAt: null,
    //     data: allAssetsData,
    //     loading: false,
    //     errors: null
    // },
    list: {
        data: assetsListData,
        loading: false,
        errors: null,
        loaded: false
    },
    loading: false,
    errors: null
});

/*
    START STATE ABM FUNCTIONS
*/

const updateAssets = (updatedAssets = []) => {
    if (!updatedAssets.length) return;

    const index = new Map(
        assets.data.map((a, i) => [a.id, i])
    );

    for (const updated of updatedAssets) {
        const idx = index.get(updated.id);

        if (idx !== undefined) {
            assets.data[idx] = {
                ...assets.data[idx],
                ...updated
            };
        }
        else {
            assets.data.push(updated);
        }
    }
};

const updateAssetsList = (updatedAssets = []) => {
    if (!updatedAssets.length) return;

    const index = new Map(
        assets.list.data.map((a, i) => [a.id, i])
    );

    for (const updated of updatedAssets) {
        const idx = index.get(updated.id);

        if (idx !== undefined) {
            assets.list.data[idx] = {
                ...assets.list.data[idx],
                ...updated
            };
        }
        else {
            assets.list.data.push(updated);
        }
    }
};

const removeAssets = (ids) => {
    let assetsToKeep = assets.data.filter(element => !ids.includes(element.id));
    let assetsListToKeep = assets.list.data.filter(element => !ids.includes(element.id));
    replaceAssets(assetsToKeep);
    replaceAssetsListElements(assetsListToKeep);
}

/*
    Orchestration logic
*/
let searchAssetsController;

export const cancelAssetSearch = () => {
    searchAssetsController?.abort();
};

export const searchAssets = async (
    resource = 'v2/asset/',
    config = { filter: {}, include_finding_count: false, buildList: {} }
) => {
    if (!config.ignore_loader) {
        assets.loading = true;
        assets.errors = null;
    }
    // Abort previous request if exists
    searchAssetsController?.abort();

    // Create local controller for this request
    const controller = new AbortController();
    searchAssetsController = controller;

    // check if this request is still the latest
    const isCurrent = () => searchAssetsController === controller;

    if (!config.filter) config.filter = {};

    if (config.filter.kind && (config.filter.kind === "all" || config.filter.kind === "allWithActions")) {
        delete config.filter.kind;
    }

    let response;

    try {
        // Pass the controller signal to the fetch
        response = await fetchAssets(resource, { ...config, signal: controller.signal });

        if (!isCurrent()) return; // discard if a newer search started

        replaceAssets(response);

    } catch (err) {
        if (err.name === 'AbortError') {
            if (isCurrent()) searchAssetsController = null; // cleanup
            return; // silently cancel
        }

        if (!isCurrent()) return;

        replaceAssets([]);
        assets.errors = err;
        return [];
    } finally {
        if (!isCurrent()) return;

        assets.loading = false;
        replaceAssetsListElements(assets.data);

        if (config.buildList) {
            assets.list.loading = true;

            if (config.buildList.properties) {
                response = await hydrateAssetsWithProps(config.filter.kind, response, controller.signal);
                if (!isCurrent()) return;
            }

            if (config.buildList.tags) {
                response = await hydrateAssetsWithTags(
                    response,
                    config.buildList.tags.elements,
                    config.buildList.tags.associations,
                    controller.signal
                );
                if (!isCurrent()) return;
            }

            replaceAssetsListElements(response);
            assets.list.loading = false;
            assets.list.loaded = true;
            if (isCurrent()) searchAssetsController = null;
            return assets.list.data;
        }

        // Cleanup controller if still current
        if (isCurrent()) searchAssetsController = null;
        return assets.list.data;
    }
};

export const searchProperties = async (config = { filter: {} }) => {
    let propertiesPromise = assetsApi.fetchAssetsProperties(config)
        .catch((e) => {
            return [];
        })
        .then(pProperties => {
            return pProperties;
        })
    return propertiesPromise;
}

export const searchAssetPropertiesCount = async (config = { filter: {} }) => {
    let propertiesPromise = assetsApi.fetchAssetsPropertiesCount(config)
        .catch((e) => {
            return [];
        })
        .then(pProperties => {
            return pProperties;
        })
    return propertiesPromise;
}

export const searchAssetPropertiesCountByKind = async (config = { filter: {} }) => {
    let propertiesPromise = assetsApi.fetchAssetsPropertiesCountByKind(config)
        .catch((e) => {
            return [];
        })
        .then(pProperties => {
            return pProperties;
        })
    return propertiesPromise;
}

export const searchRelations = async (config = { filter: {} }) => {
    let relationsPromise = assetsApi.fetchAssetsRelations(config)
        .catch((e) => {
            return [];
        })
        .then(pRelations => {
            return pRelations;
        })
    return relationsPromise;
}

export const searchRelatedAssets = async (asset_id, config) => {
    let relationsPromise = assetsApi.fetchAssetRelations(asset_id, config)
        .catch((e) => {
            return [];
        })
        .then(pRelations => {
            return pRelations;
        })
    return relationsPromise;
}

export const searchAssetsCountByKind = async (config = {}) => {
    try {
        let response = await fetchAssetsCountByKind(config);
        return response;
    }
    catch (err) {
        return err;
    }
}

export async function getAssetDetail(id, config = {}) {
    let asset;
    return findAsset(id, config)
        .catch(() => {
            return {};
        })
        .then(pAsset => {
            asset = pAsset;
            return asset;
        })
        ;
}

export const handleAssetsUpsert = async (assetsToBeSaved = [], exclude_notification = false) => {
    let errors = [];
    let assetsToUpdate = [];
    let is_upsert = false;
    const index = new Map(
        assetsData.map(asset => [asset.id, asset])
    );
    for (let assetToSave of assetsToBeSaved) {
        const existing = index.get(assetToSave.id);
        if (!existing) {
            is_upsert = true;
        }
        try {
            const response = await saveAsset(assetToSave, {}, is_upsert);
            if (!response.ok) {
                let toastConfig = {
                    data: assetToSave.name,
                    options: {
                        "timeOut": "0",
                        "extendedTimeOut": "0",
                        "showDuration": "0",
                        "hideDuration": "0",
                        "closeButton": true
                    }
                }

                let errors = await response.json();
                if (errors.includes("Domain not found")) {
                    notificate('domain_not_found', "toast", toastConfig);
                }
                else {
                    notificate('save_error', "toast", toastConfig);
                }
                throw errors;
            }
            else {
                let saved = await response.json();
                if (!saved.in_scope && saved.third_party) {
                    notificate("third_party_notification");
                }
                else if (!saved.in_scope && assetToSave.in_scope) {
                    notificate("limitReached");
                }
                else {
                    if (!exclude_notification || (exclude_notification && (saved.kind != "domain" && !saved.kind.includes("network")))) {
                        notificate('save_ok', "toast", { data: assetToSave.name, is_massive: false });
                    }
                }
                assetsToUpdate.push(saved);
            }
        }
        catch (err) {
            if (typeof (err.detail) == 'string') {
                errors.push(API.translateErrorMessages(err.detail, "elements_massive"));
            }
            else {
                // parse API errors
                errors.push(API.flatten422Errors(err));
            }
        }
        finally {
            updateAssets(assetsToUpdate);
            updateAssetsList(assetsToUpdate);
            //updateAllAssetsData(assetsToUpdate);
        }
    }
    return assetsToUpdate;
}
export const hydrateAssetsWithTags = async (data, elements, tagAssociations, signal) => {
    if (signal?.aborted) return data;

    let tags = [];
    let associations = [];

    const getTags = async (config = { filter: {} }) => {
        if (signal?.aborted) return [];
        return await tagService.search('v2/tag/', config);
    };

    const getTagAssociations = async (config = { filter: { target_object_type: "Asset" } }) => {
        if (signal?.aborted) return [];
        return await tagService.search('v2/tag/association/', config);
    };

    if (!elements || elements.length < 1) {
        tags = await getTags();
        if (signal?.aborted) return data; // stop immediately if cancelled
    } else {
        tags = elements;
    }

    if (!tagAssociations || tagAssociations.length < 1) {
        associations = await getTagAssociations();
        if (signal?.aborted) return data; // stop immediately if cancelled
    } else {
        associations = tagAssociations;
    }

    // Index tags by ID for O(1) lookups
    const tagById = new Map();
    for (const tag of tags) {
        tagById.set(tag.id, tag);
    }

    // Index associations by asset id and store all associated tag ids
    const associationsByTarget = new Map();
    for (const association of associations) {
        if (!associationsByTarget.has(association.target_object_id)) {
            associationsByTarget.set(association.target_object_id, []);
        }
        associationsByTarget.get(association.target_object_id).push(association.tag_id);
    }

    // Hydrate assets with tags
    for (const asset of data) {
        if (signal?.aborted) return data;

        const tagIds = associationsByTarget.get(asset.id) || [];

        asset.tag = tagIds
            .map(id => tagById.get(id))
            .filter(Boolean) // removes any undefined tags if a tagId wasn't found
            .sort((a, b) => a.name.localeCompare(b.name));
    }

    return data;
};

export const hydrateAssetsWithProps = async (kind, data, signal) => {
    if (!kind) return data;
    if (signal?.aborted) return data;

    function groupPropertiesByKind(propertiesToGroup) {
        let grouped = [];
        const temp = propertiesToGroup.reduce((acc, curr) => {
            const { kind } = curr;
            acc[kind] = acc[kind]
                ? { ...acc[kind], [curr.name]: curr.value }
                : { kind, [curr.name]: curr.value };
            return acc;
        }, {});

        Object.values(temp).forEach((val, idx) => {
            let { kind, ...rest } = val;
            grouped[idx] = { kind: kind, vals: rest };
        });
        return grouped;
    }

    let properties = [];
    let relations = [];
    let relatedAssets = [];
    let extraColumns = powerTableService.getAssetsColumnsDefinition(kind).extraColumns;

    // Properties
    if (extraColumns.properties.length > 0) {
        for (let col of extraColumns.properties) {
            if (signal?.aborted) return data;

            let propertiesResponse = [];
            if (kind === "domain") {
                propertiesResponse = await searchProperties({ filter: { kind: 'date' } });
                properties.push(...propertiesResponse); // push(...arr) avoids memory-heavy O(N^2) array spreading
                break;
            } else {
                propertiesResponse = await searchProperties({ filter: { kind: col.key } });
            }

            if (signal?.aborted) return data;
            properties.push(...propertiesResponse);
        }
    }

    // Relations
    if (extraColumns.relations.length > 0) {
        for (let col of extraColumns.relations) {
            if (signal?.aborted) return data;
            let relationsResponse = await searchRelations({ filter: { kind: "dns" } });
            relations.push(...relationsResponse);
        }

        for (let kindRel of extraColumns.relatedAssetsKind) {
            if (signal?.aborted) return data;
            let relatedAssetsResponse = await fetchAssets("v2/asset/", { filter: { kind: kindRel } });
            relatedAssets.push(...relatedAssetsResponse);
        }
    }

    // --- Create Hash Maps for O(1) Lookups ---

    // 1. Group properties by asset_id
    const propertiesByAssetId = new Map();
    for (const prop of properties) {
        if (!propertiesByAssetId.has(prop.asset_id)) {
            propertiesByAssetId.set(prop.asset_id, []);
        }
        propertiesByAssetId.get(prop.asset_id).push(prop);
    }

    // 2. Index related assets by ID
    const relatedAssetById = new Map();
    for (const ra of relatedAssets) {
        relatedAssetById.set(ra.id, ra);
    }

    // 3. Group relations by asset_id (both from and to sides)
    const relationsByAssetId = new Map();
    const addRelation = (assetId, rel) => {
        if (!assetId) return;
        if (!relationsByAssetId.has(assetId)) {
            relationsByAssetId.set(assetId, []);
        }
        relationsByAssetId.get(assetId).push(rel);
    };

    for (const rel of relations) {
        addRelation(rel.from_asset_id, rel);
        addRelation(rel.to_asset_id, rel);
    }

    // --- Main Asset Loop ---
    for (let asset of data) {
        if (signal?.aborted) return data;

        // Fetch via Map instead of looping with Array.filter()
        const rawProperties = propertiesByAssetId.get(asset.id) || [];
        const filteredProperties = groupPropertiesByKind(rawProperties);

        let ports = [];
        let location = [];
        let os = '';

        for (let property of filteredProperties) {
            if (signal?.aborted) return data;

            if (property.kind === 'port_status') ports.push(property);
            if (property.kind === 'os' && property.vals.name) {
                os = property.vals.name;
            }
            if (property.kind === 'location') location.push(property);
            if (property.kind === 'date') {
                for (let val of Object.values(property.vals)) {
                    asset.expiration_date = val;
                    asset.valid_until = val;
                }
            }
            if (property.kind === "netbios" && property.vals.computer_name && kind === "private_ip") {
                asset.netbios = property.vals.computer_name;
            }
        }

        asset.rels = [];

        // Fetch relations via Map instead of looping with Array.filter()
        const rels = relationsByAssetId.get(asset.id) || [];

        for (let relation of rels) {
            if (signal?.aborted) return data;

            // Find the ID of the asset on the *other* side of this relation
            const relatedId = relation.from_asset_id === asset.id
                ? relation.to_asset_id
                : relation.from_asset_id;

            if (relatedId && relatedId !== asset.id) {
                // Fetch directly from Map instead of iterating through all relatedAssets
                const relatedAsset = relatedAssetById.get(relatedId);

                if (relatedAsset) {
                    // Clone the asset object before mutating it with relationKind so it doesn't cause
                    // side effects if the same related asset is attached to multiple primary assets
                    asset.rels.push({
                        ...relatedAsset,
                        relationKind: relation.kind
                    });
                }
            }
        }

        if (assetsApi.is_ip_list(kind)) {
            asset.port_status = ports;
            asset.os = os;
        }

        if (kind === "public_ip") {
            asset.rels.sort((a, b) => a.name.localeCompare(b.name));
            asset.location = location[0] || '';
        }

        if (kind === "hostname") {
            asset.rels = sortIPAddress(asset.rels);
        }
    }

    return data;
};


export const handleAssetsDelete = async (assetsToDelete = []) => {
    let result = await notificate('confirm_delete', "modal", { massive: true, findings_warning: true });
    if (!result.isConfirmed) {
        return false;
    }
    let ids = [];
    for (let assetToDelete of assetsToDelete) {
        ids.push(assetToDelete.id);
    }
    deleteAssets(ids)
        .then(async (response) => {
            let toastConfig = {
                options: {
                    "timeOut": "0",
                    "extendedTimeOut": "0",
                    "showDuration": "0",
                    "hideDuration": "0",
                    "closeButton": true
                }
            }
            if (response.status >= 400 || !response) {
                notificate('delete_error', "toast", { ...toastConfig, is_massive: ids.length > 1 ? true : false, data: ids.length > 1 ? getStore(format)("asset.plural") : getStore(format)("asset.word") });
                throw await response.json();
            }
            else {
                notificate('delete_ok', "toast", { ...toastConfig, is_massive: ids.length > 1 ? true : false, data: ids.length > 1 ? getStore(format)("asset.plural") : getStore(format)("asset.word") });
                removeAssets(ids);
                return true;
            }
        })
        ;
}

export const handleScopeChange = async (selected_rows, value) => {
    if (selected_rows.length < 1) return notificate("select_element", "modal");
    for (let row of selected_rows) {
        if (value) {
            row.in_scope = true;
        }
        else {
            row.in_scope = false;
        }
    }
    await handleAssetsUpsert(selected_rows)
}

export const handleAutoDeleteChange = async (selected_rows, value) => {
    if (value == "cancel") return;
    if (selected_rows.length < 1) return notificate("select_element", "modal");
    for (let row of selected_rows) {
        if ((row.kind == "domain" || row.kind.includes("network"))) {
            notificate("autodelete_excluded", "toast", { data: getStore(format)(row.name) });
        }
        else {
            if (!value || value === "false") {
                row.auto_delete = false;
            }
            else {
                row.auto_delete = true;
            }
        }
    }
    await handleAssetsUpsert(selected_rows, true)
}

/* Reset */

export const replaceAssets = (newAssets) => {
    if (newAssets) {
        assetsData.length = 0;
        assetsData.push(...newAssets);
    }
};

export const replaceAssetsListElements = (newElements) => {
    if (newElements) {
        assetsListData.length = 0;
        assetsListData.push(...newElements);
    }
};


export const resetAssets = () => {
    replaceAssets([]);
    replaceAssetsListElements([]);
    assets.loading = false;
    assets.errors = null;
    assets.assetsListData.loading = false;
    assets.assetsListData.errors = null;
};
